import { useRef, useState, useEffect } from 'react';
import { X, Download, Copy } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { SeasonShareCard, type SeasonShareData } from '@/components/SeasonShareCard';
import { useShare } from '@/hooks/useShare';

interface ShareSeasonModalProps {
  open: boolean;
  onClose: () => void;
  data: SeasonShareData;
}

export function ShareSeasonModal({
  open,
  onClose,
  data,
}: ShareSeasonModalProps) {
  const shareImageRef = useRef<HTMLDivElement>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { getReferralLink } = useShare();

  useEffect(() => {
    if (open && !imageDataUrl) {
      // Generate image after modal is rendered
      setTimeout(() => generateImage(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const generateImage = async () => {
    if (!shareImageRef.current || isGenerating) return;

    setIsGenerating(true);
    try {
      // Dynamically import html2canvas only when needed
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(shareImageRef.current, {
        backgroundColor: '#1B1B1B',
        scale: 2,
        logging: false,
        width: 558,
        height: 495,
      });
      const dataUrl = canvas.toDataURL('image/png');
      setImageDataUrl(dataUrl);
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to generate share image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShareTwitter = async () => {
    const referralLink = getReferralLink();
    const totalTokens = data.junk.numberOfTokens;
    const totalChests = data.chest.numberOfChestOpened;
    const totalPoints = data.summary.totalPointsRewards;

    const tweetText = `Just completed Season ${data.seasonNumber} on @junkfun_!
🗑️ ${totalTokens} tokens | 📦 ${totalChests} chests | 🎁 ${totalPoints.toLocaleString()} points earned
Start junking: ${referralLink}`;

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');

    toast.success('Opening Twitter...');
  };

  const handleDownload = () => {
    if (!imageDataUrl) {
      toast.error('Image not ready yet');
      return;
    }

    const link = document.createElement('a');
    link.download = `junkfun-season${data.seasonNumber}-summary.png`;
    link.href = imageDataUrl;
    link.click();
    toast.success('Image downloaded!');
  };

  const handleCopyImage = async () => {
    if (!imageDataUrl) {
      toast.error('Image not ready yet');
      return;
    }

    try {
      const blob = await (await fetch(imageDataUrl)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      toast.success('Image copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy image:', error);
      toast.error('Failed to copy image. Try downloading instead.');
    }
  };

  const handleClose = () => {
    setImageDataUrl(null);
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with dim */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Close button */}
      <button
        onClick={handleClose}
        className={cn(
          'absolute top-8 right-8 z-10 p-2 rounded-full',
          'text-white/60 hover:text-white',
          'hover:bg-white/10 transition-colors'
        )}
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {/* Content */}
      <div className="relative flex flex-col items-center gap-6">
        {/* Header Text - Not included in share image */}
        <h2
          className="text-white text-center"
          style={{
            fontFamily: 'Hyperspace Race Capsule, sans-serif',
            fontSize: 32,
            textTransform: 'uppercase',
          }}
        >
          Share Your Season {data.seasonNumber} Junk Life
        </h2>

        {/* Preview */}
        <div ref={shareImageRef}>
          <SeasonShareCard data={data} />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-8">
          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={!imageDataUrl || isGenerating}
            className={cn(
              'flex flex-col items-center gap-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-transform hover:scale-105 active:scale-95'
            )}
          >
            <div
              className={cn(
                'w-16 h-16 rounded-full flex items-center justify-center',
                'bg-black transition-all',
                'border-2 border-white hover:border-white/80'
              )}
            >
              <Download size={28} className="text-white" />
            </div>
            <span
              className="text-white text-sm font-medium"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Download
            </span>
          </button>

          {/* Copy Button */}
          <button
            onClick={handleCopyImage}
            disabled={!imageDataUrl || isGenerating}
            className={cn(
              'flex flex-col items-center gap-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-transform hover:scale-105 active:scale-95'
            )}
          >
            <div
              className={cn(
                'w-16 h-16 rounded-full flex items-center justify-center',
                'bg-black transition-all',
                'border-2 border-white hover:border-white/80'
              )}
            >
              <Copy size={28} className="text-white" />
            </div>
            <span
              className="text-white text-sm font-medium"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Copy
            </span>
          </button>

          {/* Share on X Button */}
          <button
            onClick={handleShareTwitter}
            disabled={!imageDataUrl || isGenerating}
            className={cn(
              'flex flex-col items-center gap-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-transform hover:scale-105 active:scale-95'
            )}
          >
            <div
              className={cn(
                'w-16 h-16 rounded-full flex items-center justify-center',
                'bg-black transition-all',
                'border-2 border-white hover:border-white/80'
              )}
            >
              <img src="/prime_twitter.svg" alt="X" className="w-7 h-7" />
            </div>
            <span
              className="text-white text-sm font-medium"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Share to X
            </span>
          </button>
        </div>

        {isGenerating && (
          <div className="text-center">
            <p className="text-white/60 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Generating image...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
